// Direct native health sync: iOS HealthKit (SolanoHealth, HealthKitPlugin.swift)
// or Android Health Connect (SolanoHealthConnect, HealthConnectPlugin.kt). Both
// return identical per-day rows that map 1:1 onto POST /api/client/apple-health/native-sync.
import { Capacitor, registerPlugin } from "@capacitor/core";
import { api } from "@/lib/http";

const SolanoHealth = registerPlugin("SolanoHealth");
const SolanoHealthConnect = registerPlugin("SolanoHealthConnect");

const platform = () => { try { return Capacitor.getPlatform(); } catch { return "web"; } };
const nativeHealth = () => (platform() === "android" ? SolanoHealthConnect : SolanoHealth);
export const isHealthConnect = () => platform() === "android";

const AUTO_SYNC_KEY = "solano_hk_last_sync";
const AUTO_SYNC_INTERVAL_MS = 6 * 60 * 60 * 1000; // 6h

export const isNativeHealthSupported = () => {
  const p = platform();
  return p === "ios" || p === "android";
};

/** Health Connect app missing/outdated → open its Play Store page. */
export async function openHealthConnectStore() {
  if (!isHealthConnect()) return;
  try { await SolanoHealthConnect.openStore(); } catch { /* ignore */ }
}

/** Health Connect home (data sources + app permissions). */
export async function openHealthConnectSettings() {
  if (!isHealthConnect()) return;
  try { await SolanoHealthConnect.openSettings(); } catch { /* ignore */ }
}

/** Reads the last `daysBack` days from HealthKit and uploads them. */
const fail = (stage, detail, cause) => {
  const err = new Error(`${stage}: ${detail}`);
  err.stage = stage;
  err.detail = detail;
  err.cause = cause;
  return err;
};

export async function syncNativeHealth(daysBack = 7) {
  let days, errors = [], authErr = null, res = null;
  const plugin = nativeHealth();
  try {
    const avail = await plugin.isAvailable();
    if (!avail?.available) throw fail("healthkit", avail?.reason || "unavailable");
    // Authorization prompt is best-effort: if it was granted earlier, reading
    // still works even when the OS refuses to re-show the sheet.
    try { await plugin.requestAuthorization({}); } catch (e) { authErr = e; }
    res = await plugin.dailyMetrics({ daysBack });
    days = res?.days;
    errors = Array.isArray(res?.errors) ? res.errors : [];
  } catch (e) {
    if (e?.stage) throw e;
    const msg = e?.message || e?.code || String(e);
    if (/permission_denied/i.test(msg)) throw fail("healthkit", "permission_denied", e);
    throw fail("healthkit", msg, e);
  }
  const platformName = isHealthConnect() ? "health_connect" : "healthkit";
  if (!days || !Object.keys(days).length) {
    const reason = [authErr && (authErr.message || authErr.code), ...errors].filter(Boolean).join(" | ");
    if (reason) throw fail("healthkit", reason);
    // Permissions fine, but the store holds no rows for the range. Still
    // register the connection server-side so the card shows "connected", then
    // let the UI explain that a source app (Samsung Health, Fitbit, …) must sync.
    try { await api.post("/client/apple-health/native-sync", { days: {}, platform: platformName }); } catch { /* best effort */ }
    try { localStorage.setItem(AUTO_SYNC_KEY, String(Date.now())); } catch {}
    return { ok: true, days_updated: 0, empty: true, granted: res?.granted_count ?? null, record_counts: res?.record_counts || {} };
  }
  try {
    const { data } = await api.post("/client/apple-health/native-sync", { days, platform: platformName });
    try { localStorage.setItem(AUTO_SYNC_KEY, String(Date.now())); } catch {}
    return data;
  } catch (e) {
    const status = e?.response?.status;
    if (status) throw fail("server", `HTTP ${status} ${e?.response?.data?.detail || ""}`.trim(), e);
    throw fail("network", e?.message || "offline", e);
  }
}

/** Maps a syncNativeHealth error onto a localized, stage-specific message. */
export function describeHealthSyncError(e, t) {
  const detail = e?.detail ? ` (${String(e.detail).slice(0, 160)})` : "";
  if (e?.stage === "server")
    return (t("wearable.healthkitServerError") || "Server-Fehler beim Speichern der Health-Daten. Bitte später erneut versuchen.") + detail;
  if (e?.stage === "network")
    return (t("wearable.healthkitNetworkError") || "Keine Verbindung zum Server. Prüfe deine Internetverbindung.") + detail;
  if (isHealthConnect()) {
    if (e?.detail === "update_required" || e?.detail === "unavailable")
      return t("wearable.healthConnectMissing") + detail;
    if (e?.detail === "permission_denied")
      return t("wearable.healthConnectNoPermission");
    return t("wearable.healthConnectDenied") + detail;
  }
  return (t("wearable.healthkitDenied") || "HealthKit-Zugriff nicht möglich. Prüfe die Freigaben in Einstellungen → Health → Datenzugriff.") + detail;
}

/** Silent background sync — at most once every 6h, never throws. */
export async function autoSyncNativeHealthIfDue() {
  if (!isNativeHealthSupported()) return;
  try {
    const last = Number(localStorage.getItem(AUTO_SYNC_KEY) || 0);
    if (Date.now() - last < AUTO_SYNC_INTERVAL_MS) return;
    await syncNativeHealth(7);
  } catch { /* silent — user can always sync manually */ }
}
